<template>
  <div
    class="flex flex-grow flex-col-reverse gap-6 md:flex-row md:items-center md:justify-between"
  >
    <div
      class="flex flex-wrap items-center gap-x-10 gap-y-4 text-sm font-semibold"
      data-testid="checkboxes"
    >
      <CommonFormCheckbox v-model="allCategories" label="All" />
      <CommonFormCheckbox
        v-for="category in categories"
        :key="category.slug"
        :data="category"
        :label="category.name"
        :model-value="isActiveCategory(category)"
        @change="changeActiveCategories"
      />
    </div>

    <CommonSearch
      v-if="withSearch"
      v-model="searchValue"
      data-testid="search"
      class="md:max-w-[200px]"
      @input="changeSearch"
    />
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue'
import debounce from 'lodash/debounce'

import type { TGeneral } from '~/src/types/common'

const props = withDefaults(
  defineProps<{
    withSearch?: boolean
    activatedCategories?: any
    search?: string
    categories: TGeneral[]
  }>(),
  {
    withSearch: false,
    activatedCategories: [],
    search: '',
  },
)

const emit = defineEmits<{
  (event: 'search', value: string): void
  (event: 'changeCategories', categories: TGeneral[]): void
}>()

const searchValue = ref<string>(props.search)
const selectedCategories = ref<TGeneral[]>(props.activatedCategories)

const allCategories = computed<boolean>({
  get() {
    return selectedCategories.value.length === props.categories.length
  },
  set(value: boolean) {
    selectedCategories.value = value ? [...props.categories] : []
    emit('changeCategories', selectedCategories.value)
  },
})

const isActiveCategory = (category: TGeneral): boolean => {
  return selectedCategories.value.some((item) => item.slug === category.slug)
}

const changeActiveCategories = (category: TGeneral): void => {
  const foundIndex = selectedCategories.value.findIndex(
    (item) => item.slug === category.slug,
  )

  if (foundIndex >= 0) {
    selectedCategories.value = selectedCategories.value.filter(
      (item) => item.slug !== category.slug,
    )
  } else {
    selectedCategories.value = [...selectedCategories.value, category]
  }
  emit('changeCategories', selectedCategories.value)
}

const changeSearch = debounce(() => {
  emit('search', searchValue.value)
}, 1000)

onMounted(() => {
  selectedCategories.value = props.activatedCategories
  searchValue.value = props.search
})
</script>
